import type { CaseNotice, DomainNotice, SearchType } from "./types";

const DEFAULT_API_BASE_URL = "https://api.sunset.ncvic.org";

interface TosViolation {
  name: string;
  url: string | null;
}

interface PublicRegistryRecord {
  search_type: SearchType;
  case_number: string;
  legal_violations: string[];
  tos_violations: TosViolation[];
  date_reported: string;
  domain: string | null;
  matched_url: string | null;
  url_status: string | null;
}

interface RegistryAppealInput {
  referenceType: SearchType;
  reference: string;
  name: string;
  email: string;
  relationship: string;
  relationshipOther?: string;
  reason: string;
}

const DEMO_CASE_NUMBER = "NCVIC-2024-001234";
const DEMO_DOMAIN = "illegalcontent.com";

const demoCaseNotice: CaseNotice = {
  caseNumber: DEMO_CASE_NUMBER,
  reason:
    "This content was removed following a lawful request citing violations of the following:",
  legalViolations: [
    "18 U.S.C. § 1591 – Sex trafficking of children by force, fraud, or coercion",
    "18 U.S.C. § 1594 – Sex trafficking conspiracy",
    "18 U.S.C. § 2251 – Sexual exploitation of children",
    "18 U.S.C. § 1595 – Civil action against financial beneficiaries of trafficking venture",
    "47 U.S.C. § 223(h) – Criminal intentional disclosure of nonconsensual intimate depiction (Take It Down Act)",
    "15 U.S.C. § 6851 – Civil action relating to disclosure of intimate images",
  ],
  termsViolations: [
    "Cloudflare Terms of Service",
    "Google Terms of Service",
    "Amazon Web Services Acceptable Use Policy",
  ],
  dateReported: "November 15, 2024",
};

const demoDomainNotice: DomainNotice = {
  domain: DEMO_DOMAIN,
  status: "Seized",
  reason:
    "This domain was seized following a lawful request citing violations of the following:",
  legalViolations: [
    "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
    "18 U.S.C. § 2252A (Child Sexual Abuse Material)",
    "18 U.S.C. § 1029 (Fraud and Related Activity in Connection with Access Devices)",
  ],
  termsViolations: [
    "Cloudflare Terms of Service",
    "Google Terms of Service",
    "Domain Registrar Acceptable Use Policy",
  ],
  provider: "Federal Bureau of Investigation (FBI)",
  actionDate: "November 20, 2024",
  caseNumber: DEMO_CASE_NUMBER,
};

export class RegistryApiError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "RegistryApiError";
    this.status = status;
  }
}

function apiBaseUrl() {
  const configured = import.meta.env["VITE_API_BASE_URL"] as unknown;
  if (typeof configured === "string" && configured.trim()) {
    return configured.trim().replace(/\/+$/, "");
  }
  return DEFAULT_API_BASE_URL;
}

function isDemoCase(searchType: SearchType, query: string) {
  return (
    searchType === "case" &&
    query.trim().toUpperCase() === DEMO_CASE_NUMBER.toUpperCase()
  );
}

function isDemoDomain(searchType: SearchType, query: string) {
  return (
    searchType === "domain" &&
    query.trim().toLowerCase() === DEMO_DOMAIN.toLowerCase()
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatStatus(value: string | null) {
  if (!value) {
    return "Registry record found";
  }
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function mapTermsViolations(violations: TosViolation[]) {
  return violations
    .map((violation) => violation.name.trim())
    .filter((name) => name.length > 0);
}

function mapRegistryRecord(
  record: PublicRegistryRecord,
  requestedType: SearchType,
  query: string
): CaseNotice | DomainNotice {
  const reason =
    "This content was removed following a lawful request citing violations of the following:";
  const termsViolations = mapTermsViolations(record.tos_violations);

  if (requestedType === "domain") {
    return {
      domain: record.domain ?? query,
      status: formatStatus(record.url_status),
      reason,
      legalViolations: record.legal_violations,
      termsViolations,
      actionDate: formatDate(record.date_reported),
      caseNumber: record.case_number,
      ...(record.matched_url ? { matchedUrl: record.matched_url } : {}),
    };
  }

  return {
    caseNumber: record.case_number,
    reason,
    legalViolations: record.legal_violations,
    termsViolations,
    dateReported: formatDate(record.date_reported),
  };
}

export async function searchPublicRegistry(
  searchType: SearchType,
  query: string
) {
  const trimmedQuery = query.trim();
  if (isDemoCase(searchType, trimmedQuery)) {
    return { ...demoCaseNotice };
  }
  if (isDemoDomain(searchType, trimmedQuery)) {
    return { ...demoDomainNotice };
  }

  const params = new URLSearchParams({
    type: searchType,
    q: trimmedQuery,
  });

  const response = await fetch(
    `${apiBaseUrl()}/api/v1/public/registry?${params.toString()}`
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new RegistryApiError("Registry lookup failed.", response.status);
  }

  const record = (await response.json()) as PublicRegistryRecord;
  return mapRegistryRecord(record, searchType, trimmedQuery);
}

export async function submitRegistryAppeal(input: RegistryAppealInput) {
  if (isDemoCase(input.referenceType, input.reference)) {
    return { status: "demo-received", reference: DEMO_CASE_NUMBER };
  }
  if (isDemoDomain(input.referenceType, input.reference)) {
    return { status: "demo-received", reference: DEMO_CASE_NUMBER };
  }

  const response = await fetch(`${apiBaseUrl()}/api/v1/public/registry/appeals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      reference_type: input.referenceType,
      reference: input.reference,
      name: input.name,
      email: input.email,
      relationship: input.relationship,
      relationship_other: input.relationshipOther?.trim() || null,
      reason: input.reason,
    }),
  });

  if (!response.ok) {
    throw new RegistryApiError("Appeal submission failed.", response.status);
  }

  return response.json() as Promise<{ status: string; reference: string }>;
}
